/*
 * Cart line updates (remove / quantity / edit options) for the cart page and
 * the drawer.
 *
 * The theme routes every remove click and every quantity change through
 * Shopify.removeItem / Shopify.changeItem, which POST /cart/change and then
 * hand the result to a callback that fetches the whole cart section again
 * (GET /cart?section_id=... on the page, GET /cart?view=ajax_side_cart in the
 * drawer). On this store a cart write is ~1s, so that was two round trips and
 * ~1.5-2s before anything on screen changed - and when the free stud was then
 * auto-removed by the sync script, only the drawer was refreshed, so the cart
 * page kept showing a stud that was no longer in the cart.
 *
 * This replaces those two functions:
 *   - one write, with the Section Rendering API returning the drawer and the
 *     cart-page section in the same response; no second fetch
 *   - when the change leaves the cart below the free-gift threshold (or with
 *     no real product at all), the stud is removed in the same write via
 *     /cart/update.js, so the page never shows a stud that is about to vanish
 *   - window.civiaCart exposes the renderers so the sync script in
 *     layout/theme.liquid can paint its own gift add/remove the same way
 *
 * It also owns the "Edit Option" popup's Add to Cart button (the pencil icon
 * on a cart line). The theme's own handler removed the line first and only
 * added the newly chosen variant inside the remove callback - a callback the
 * override above no longer fires, which left the popup spinning forever with
 * the item gone from the cart. Even before that it was remove -> synchronous
 * XHR add -> third request to read the cart. The edit flow below adds the new
 * variant first (so a failed add can never lose the shopper's item), keeps the
 * line's properties such as the ring size, then adjusts or removes the
 * original line by its key, and paints the result from that last response.
 * Changing only the quantity, or re-adding the same variant, is a single
 * write.
 */
(function () {
  var GIFT_VARIANT_ID = 50154028728568;
  var MIN_CART_TOTAL = 149900; // $1,499.00, in cents - keep in step with layout/theme.liquid
  var DRAWER_SECTION = 'civia-cart-drawer';
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var LOADING =
    '<div class="loading-overlay loading-overlay--custom"><div class="loading-overlay__spinner">' +
    '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div>';

  function root() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || (window.routes && routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function pageSectionId() {
    var el = document.querySelector('cart-items[data-section-id]');
    return el ? el.dataset.sectionId : null;
  }

  function sectionIds() {
    var ids = [DRAWER_SECTION];
    var page = pageSectionId();
    if (page) ids.push(page);
    return ids;
  }

  function drawerWrapper() {
    return document.querySelector('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');
  }

  function pageRoot() {
    return document.getElementById('main-cart-items');
  }

  /* innerHTML does not execute <script>; the rows carry the stock arrays the stepper reads. */
  function runInlineScripts(container) {
    if (!container) return;
    container.querySelectorAll('script').forEach(function (old) {
      var s = document.createElement('script');
      s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    });
  }

  function setCount(n) {
    n = parseInt(n, 10);
    if (isNaN(n)) return;
    var strings = window.cartStrings || {};
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n >= 100 && strings.item_99 ? strings.item_99 : n;
    });
    document.querySelectorAll('[data-cart-text]').forEach(function (el) {
      el.textContent = n === 1 ? strings.item || '' : strings.items || '';
    });
  }

  function setLoading(on) {
    [drawerWrapper(), pageRoot()].forEach(function (el) {
      if (!el) return;
      el.classList.toggle('is-loading', on);
      el.querySelectorAll(':scope > .loading-overlay').forEach(function (o) { o.remove(); });
      if (on) el.insertAdjacentHTML('afterbegin', LOADING);
    });
  }

  /* The drawer's list is its own scroll container (.previewCart, overflow:auto). */
  function drawerScroller() {
    var wrap = drawerWrapper();
    return wrap ? wrap.querySelector('.previewCart') : null;
  }

  /* Returns the item count the section was rendered with, so callers need no cart object. */
  function renderDrawer(html) {
    var wrap = drawerWrapper();
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var src = doc.querySelector('[data-cart-drawer-root]');
    if (!src) return null;
    if (wrap) {
      // Replacing the markup resets the list to the top. With two lines in the
      // cart the second one's stepper sits below the fold, so that threw the
      // shopper back above it after every single tap.
      var scroller = drawerScroller();
      var keepTop = scroller ? scroller.scrollTop : 0;
      wrap.classList.remove('is-loading');
      wrap.innerHTML = src.innerHTML;
      runInlineScripts(wrap);
      var next = drawerScroller();
      if (next && keepTop) next.scrollTop = keepTop;
    }
    return src.getAttribute('data-cart-item-count');
  }

  /* Mirrors the theme's halo.updateCart(): swap the list and the totals, or the whole block when the cart is empty. */
  function renderPage(html, itemCount) {
    var target = pageRoot();
    if (!target) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var src = doc.getElementById('main-cart-items');
    if (!src) return;

    var keepY = window.scrollY;

    target.classList.remove('is-loading');
    target.querySelectorAll(':scope > .loading-overlay').forEach(function (o) { o.remove(); });

    var list = target.querySelector('[data-cart-content] .cart');
    var newList = src.querySelector('[data-cart-content] .cart');
    if (itemCount > 0 && list && newList) {
      list.innerHTML = newList.innerHTML;
      runInlineScripts(list);
      ['.cart-total-subtotal', '.cart-total-grandtotal', '.cart-total-savings'].forEach(function (sel) {
        var to = target.querySelector('[data-cart-total] ' + sel);
        var from = src.querySelector('[data-cart-total] ' + sel);
        if (to && from) {
          to.innerHTML = from.innerHTML;
          to.style.display = from.getAttribute('style') && /display:\s*none/.test(from.getAttribute('style')) ? 'none' : '';
        }
      });
      var calcTo = target.querySelector('.haloCalculatorShipping');
      var calcFrom = src.querySelector('.haloCalculatorShipping');
      if (calcTo && calcFrom) calcTo.replaceWith(calcFrom);
    } else {
      target.innerHTML = src.innerHTML;
      var header = document.querySelector('.page-header');
      var newHeader = doc.querySelector('.page-header');
      if (header && newHeader) header.innerHTML = newHeader.innerHTML;
    }

    if (keepY) window.scrollTo(0, keepY);
  }

  /*
   * Paints whatever sections the write returned. `cart` is optional: /cart/add.js
   * answers with the added lines only, so the item count is taken from the
   * rendered drawer section when the response has none.
   */
  function renderSections(sections, cart) {
    if (!sections) return;
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    if (sections[DRAWER_SECTION]) {
      var rendered = renderDrawer(sections[DRAWER_SECTION]);
      if (count === null && rendered !== null) count = parseInt(rendered, 10);
    }
    var page = pageSectionId();
    if (page && sections[page]) renderPage(sections[page], count === null ? 1 : count);
    if (count !== null) {
      setCount(count);
      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart && typeof cart.item_count === 'number' ? cart : { item_count: count } }));
    }
  }

  function warn(message) {
    if (typeof window.showWarning === 'function') window.showWarning(message, window.warningTime || 3000);
    else console.error(message);
  }

  /* POST to the Cart API; resolves with the parsed body, rejects with a message on a non-2xx answer. */
  function post(path, payload) {
    return fetch(root() + path, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload) })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var error = new Error(data.description || data.message || 'Your cart could not be updated.');
            error.handled = true;
            throw error;
          }
          return data;
        });
      });
  }

  /* The last write of a multi-step change carries the sections, so the screen is painted once, from the final cart. */
  function withSections(payload) {
    payload.sections = sectionIds().join(',');
    payload.sections_url = window.location.pathname;
    return payload;
  }

  /*
   * Decide whether this change also has to take the free stud out: the line
   * being changed is a real product, a stud is in the cart, and after the change
   * the cart either has no real product left or its real subtotal drops under
   * the threshold. Uses the sync script's snapshot, so it costs no request; if
   * the snapshot is missing the sync script cleans up afterwards instead.
   */
  function giftToDrop(line, quantity) {
    var cart = window.__civiaCartSnapshot;
    if (!cart || !cart.items || !cart.items[line - 1]) return null;
    var target = cart.items[line - 1];
    if (target.variant_id === GIFT_VARIANT_ID) return null;
    var gift = cart.items.filter(function (i) { return i.variant_id === GIFT_VARIANT_ID; })[0];
    if (!gift) return null;

    var realTotal = 0;
    var realLines = 0;
    cart.items.forEach(function (i) {
      if (i.variant_id === GIFT_VARIANT_ID) return;
      if (i.key === target.key) {
        realTotal += i.original_price * quantity;
        if (quantity > 0) realLines += 1;
      } else {
        realTotal += i.original_line_price;
        realLines += 1;
      }
    });
    return realLines === 0 || realTotal < MIN_CART_TOTAL ? { target: target, gift: gift } : null;
  }

  function write(line, quantity) {
    line = parseInt(line, 10);
    quantity = Math.max(0, parseInt(quantity, 10) || 0);

    var drop = giftToDrop(line, quantity);
    var payload, path;
    if (drop) {
      var updates = {};
      updates[drop.target.key] = quantity;
      updates[drop.gift.key] = 0;
      payload = { updates: updates };
      path = 'cart/update.js';
    } else {
      payload = { line: line, quantity: quantity };
      path = 'cart/change.js';
    }

    setLoading(true);
    return post(path, withSections(payload))
      .then(function (cart) {
        window.__civiaCartSnapshot = cart;
        renderSections(cart.sections, cart);
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        if (error && error.handled) {
          warn(error.message);
          return;
        }
        console.error('Cart update failed:', error);
        window.location.reload();
      });
  }

  /* ------------------------------------------------------------------ */
  /* Quantity stepper                                                    */
  /* ------------------------------------------------------------------ */

  /*
   * Why this exists.
   *
   * A tap on - or + used to cost ~2.2s before the number on screen was the
   * shopper's: global.js waits 350ms, dispatches a change event, theme.js turns
   * that into Shopify.changeItem, and only then does the ~1.3s write to Shopify
   * start - with the row replaced wholesale at the end of it. Nothing moved in
   * between, so on a phone the natural response is to tap again, and each extra
   * tap queued another full write behind the first.
   *
   * This owns the stepper instead:
   *   - the number changes on the tap, not 2.2s later
   *   - a burst of taps is one write for the final quantity, not one per tap
   *   - the write is addressed by line key, so the free stud being added or
   *     removed underneath the shopper cannot renumber the lines and send the
   *     change to the wrong product
   *   - no blocking overlay, so the stepper stays usable while Shopify answers
   */

  var QTY_DEBOUNCE = 400;
  var pending = {}; // line key -> { quantity, timer }

  /*
   * Renders `cents` in the same shape as a money string the theme already
   * produced ("$1,079.00" -> "$2,158.00"). Reading the format off the node is
   * safer than formatting from scratch: a shop can have more than one money
   * format configured and there is no way to tell which one rendered a node.
   */
  function formatLike(sample, cents) {
    // The whitespace class covers the space, NBSP and narrow-NBSP that some
    // locales use as a thousands separator.
    var match = /\d[\d.,\s']*\d|\d/.exec(sample);
    if (!match) return null;
    var num = match[0];
    var sepAt = Math.max(num.lastIndexOf('.'), num.lastIndexOf(','));
    var decimals = 0;
    var decSep = '.';
    var groupSep = '';
    if (sepAt > -1) {
      if (num.length - sepAt - 1 === 2) {
        decSep = num.charAt(sepAt);
        decimals = 2;
        groupSep = decSep === '.' ? ',' : '.';
      } else {
        groupSep = num.charAt(sepAt);
      }
    }
    var value = Math.abs(Math.round(cents));
    var whole = decimals === 2 ? Math.floor(value / 100) : Math.round(value / 100);
    var text = String(whole);
    if (groupSep) text = text.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    if (decimals === 2) text += decSep + ('0' + (value % 100)).slice(-2);
    return sample.replace(num, text);
  }

  /* Subtotal is cart.original_total_price; total is cart.total_price. Both surfaces. */
  function moneyNodes(which) {
    var sel = which === 'subtotal'
      ? '#halo-cart-sidebar [data-cart-subtotal], .cart-total-subtotal .cart-total-value'
      : '#halo-cart-sidebar [data-cart-total-value], .cart-total-grandtotal .cart-total-value';
    return document.querySelectorAll(sel);
  }

  function setMoney(which, cents) {
    moneyNodes(which).forEach(function (el) {
      var next = formatLike(el.textContent, cents);
      if (next !== null) el.textContent = next;
      if (which === 'total' && el.hasAttribute('data-cart-total-value')) {
        el.setAttribute('data-cart-total-value', cents);
      }
    });
  }

  /*
   * The cart totals as they will be once the pending quantities land, worked out
   * from Shopify's own per-unit figures in the snapshot. Anything the snapshot
   * cannot be decomposed into (a cart-level adjustment) is carried through as a
   * fixed offset rather than guessed at.
   */
  function projectedTotals() {
    var cart = window.__civiaCartSnapshot;
    if (!cart || !cart.items || !cart.items.length) return null;
    var baseSub = 0, baseTot = 0, sub = 0, tot = 0;
    cart.items.forEach(function (item) {
      var q = pending[item.key] ? pending[item.key].quantity : item.quantity;
      baseSub += item.original_price * item.quantity;
      baseTot += item.final_price * item.quantity;
      sub += item.original_price * q;
      tot += item.final_price * q;
    });
    return {
      subtotal: sub + (cart.original_total_price - baseSub),
      total: tot + (cart.total_price - baseTot)
    };
  }

  /*
   * Move the money with the number. Without this the drawer showed the new
   * quantity against the old subtotal for the ~1.3s the write takes - two
   * contradictory numbers, which reads as the cart being broken.
   */
  function showTotals() {
    var next = projectedTotals();
    if (!next) return;
    setMoney('subtotal', next.subtotal);
    setMoney('total', next.total);
  }

  /*
   * True when the cart Shopify returned has exactly the lines that are on screen,
   * so only numbers can have changed. A cart-level discount is excluded: its
   * savings row is not something we can recompute, so those carts take the full
   * re-render path instead.
   */
  function lineSetUnchanged(cart) {
    if (!cart || !cart.items) return false;
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) return false;
    var keys = cart.items.map(function (i) { return i.key; });
    var drawerRows = document.querySelectorAll('#halo-cart-sidebar .previewCartItem');
    var pageRows = document.querySelectorAll('#main-cart-items .cart-item');
    if (!drawerRows.length && !pageRows.length) return false;
    if (drawerRows.length && drawerRows.length !== keys.length) return false;
    if (pageRows.length && pageRows.length !== keys.length) return false;
    var ok = true;
    document.querySelectorAll('input[data-cart-quantity][data-line]').forEach(function (el) {
      if (keys.indexOf(el.dataset.line) === -1) ok = false;
    });
    return ok;
  }

  /* Correct the numbers in place - no innerHTML, so no scroll reset and no thumbnail reload. */
  function syncFromCart(cart) {
    cart.items.forEach(function (item) {
      // A line the shopper has tapped again since this write started is already
      // showing the newer number; writing the server's older one back would make
      // the stepper visibly bounce.
      if (!pending[item.key]) showQuantity(item.key, item.quantity);
    });
    setMoney('subtotal', cart.original_total_price);
    setMoney('total', cart.total_price);
    setCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
    if (Object.keys(pending).length) showTotals();
  }

  /* Re-apply anything the shopper tapped while the write was in flight. */
  function repaintPending() {
    var keys = Object.keys(pending);
    if (!keys.length) return;
    keys.forEach(function (key) { showQuantity(key, pending[key].quantity); });
    showTotals();
  }

  /* Cart keys are variantId:hex, so they are safe to interpolate into a selector. */
  function inputsForKey(key) {
    return document.querySelectorAll('input[data-cart-quantity][data-line="' + key + '"]');
  }

  /*
   * The ceiling for this line, or null when there is none.
   *
   * Only a variant that denies overselling is capped. The theme's own handler
   * read window.cart_selling_array_<productId> without checking it existed and
   * threw a TypeError when it did not; a missing array here just means no cap.
   */
  function maxAllowed(input) {
    var stock = parseInt(input.dataset.inventoryQuantity, 10);
    if (isNaN(stock) || stock <= 0) return null;
    var stepper = input.closest('cart-update-quantity');
    var policy = stepper && window['cart_selling_array_' + stepper.dataset.product];
    if (!policy || policy[input.dataset.cartQuantityId] !== 'deny') return null;
    return stock;
  }

  function warnStock(max) {
    var strings = window.cartStrings || {};
    if (strings.addProductOutQuantity) warn(strings.addProductOutQuantity.replace('[maxQuantity]', max));
  }

  /* The cart page draws a mobile block and a desktop block for the same line; both carry the key. */
  function showQuantity(key, quantity) {
    inputsForKey(key).forEach(function (input) { input.value = quantity; });
  }

  /* giftToDrop(), addressed by line key instead of by line number. */
  function giftToDropForKey(key, quantity) {
    var cart = window.__civiaCartSnapshot;
    if (!cart || !cart.items) return null;
    var index = -1;
    cart.items.forEach(function (item, n) { if (item.key === key) index = n; });
    if (index < 0) return null;
    return giftToDrop(index + 1, quantity);
  }

  /* The number on screen is now ahead of the cart: repaint from what Shopify actually has. */
  function resync() {
    var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds().join(','));
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (sections) { renderSections(sections, null); })
      .catch(function () { window.location.reload(); });
  }

  function writeQuantity(key, quantity) {
    var drop = giftToDropForKey(key, quantity);
    var payload, path;
    if (drop) {
      var updates = {};
      updates[key] = quantity;
      updates[drop.gift.key] = 0;
      payload = { updates: updates };
      path = 'cart/update.js';
    } else {
      payload = { id: key, quantity: quantity };
      path = 'cart/change.js';
    }

    return post(path, withSections(payload))
      .then(function (cart) {
        window.__civiaCartSnapshot = cart;
        // The screen was already painted when the shopper tapped. If the cart came
        // back with the same lines, only numbers can differ, so correct those and
        // leave the DOM - and the shopper's scroll position - alone.
        if (lineSetUnchanged(cart)) {
          syncFromCart(cart);
        } else {
          renderSections(cart.sections, cart);
          repaintPending();
        }
      })
      .catch(function (error) {
        if (error && error.handled) {
          warn(error.message);
          return resync();
        }
        console.error('Cart update failed:', error);
        window.location.reload();
      });
  }

  function releaseHold() {
    if (Object.keys(pending).length) return;
    window.__civiaCartHold = false;
    if (typeof window.civiaCartSync === 'function') window.civiaCartSync();
  }

  /* One write per burst: four taps on + send quantity 5 once, not four writes back to back. */
  function queueWrite(key, quantity) {
    var entry = pending[key] || (pending[key] = { quantity: quantity, timer: null });
    entry.quantity = quantity;
    window.__civiaCartHold = true; // hold the background gift sync until the burst lands
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(function () {
      var q = entry.quantity;
      delete pending[key];
      writeQuantity(key, q).then(releaseHold, releaseHold);
    }, QTY_DEBOUNCE);
  }

  function applyQuantity(input, quantity) {
    var key = input.dataset.line;
    if (!key) return;
    var max = maxAllowed(input);
    if (max !== null && quantity > max) {
      quantity = max;
      warnStock(max);
    }
    if (quantity < 1) quantity = 1; // emptying a line is the X button's job
    showQuantity(key, quantity);
    queueWrite(key, quantity); // before showTotals: it reads the pending quantities
    showTotals();
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.btn-quantity');
    if (!button) return;
    var stepper = button.closest('cart-update-quantity');
    if (!stepper) return;
    var input = stepper.querySelector('input[data-cart-quantity]');
    if (!input) return; // quick-shop stepper - not a cart line, leave it to the theme

    // Capture phase: stopping propagation here means neither global.js's own
    // listener on this button nor theme.js's delegated change handler runs, so
    // the 350ms delay and the second write they would cause never happen.
    event.preventDefault();
    event.stopPropagation();

    var key = input.dataset.line;
    var current = pending[key] ? pending[key].quantity : parseInt(input.value, 10);
    if (isNaN(current)) current = 1;

    var next = button.classList.contains('plus') ? current + 1 : current - 1;
    if (next < 1) next = 1;
    if (next === current) return;

    applyQuantity(input, next);
  }, true);

  /* A number typed straight into the field takes the same key-addressed path. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input[data-cart-quantity]')) return;
    if (!input.closest('cart-update-quantity')) return;
    if (input.closest('.quick-order-list__contents')) return;

    event.preventDefault();
    event.stopPropagation();

    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity)) quantity = 1;
    applyQuantity(input, quantity);
  }, true);

  /* ------------------------------------------------------------------ */
  /* Edit Option popup                                                   */
  /* ------------------------------------------------------------------ */

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        window.__civiaCartSnapshot = cart;
        return cart;
      });
  }

  /* Finds the cart line the popup was opened for; re-reads the cart if the snapshot does not have it. */
  function findLine(key) {
    var snap = window.__civiaCartSnapshot;
    var hit = snap && snap.items ? snap.items.filter(function (i) { return i.key === key; })[0] : null;
    if (hit) return Promise.resolve({ cart: snap, line: hit });
    return getCart().then(function (cart) {
      return { cart: cart, line: cart.items.filter(function (i) { return i.key === key; })[0] || null };
    });
  }

  /* Unit price of a variant from the popup's own variant JSON, for the free-gift decision. */
  function variantPrice(item, variantId) {
    var json = item.querySelector('script[type="application/json"]');
    if (!json) return null;
    try {
      var variant = JSON.parse(json.textContent).filter(function (v) { return String(v.id) === String(variantId); })[0];
      return variant ? variant.price : null;
    } catch (e) {
      return null;
    }
  }

  /* What the shopper picked in the popup: one entry per product row that is in stock. */
  function readPopupItems(wrap) {
    var items = [];
    wrap.querySelectorAll('.product-edit-item.isChecked').forEach(function (row) {
      var idInput = row.querySelector('input[name="id"]');
      var qtyInput = row.querySelector('input[name="quantity"]');
      if (!idInput || !idInput.value) return;
      var quantity = parseInt(qtyInput && qtyInput.value, 10);
      items.push({
        id: parseInt(idInput.value, 10),
        quantity: quantity > 0 ? quantity : 1,
        price: variantPrice(row, idInput.value)
      });
    });
    return items;
  }

  function lineProperties(line) {
    var out = {};
    var props = line.properties || {};
    Object.keys(props).forEach(function (k) {
      if (k !== '_free_gift' && props[k] !== null && props[k] !== '') out[k] = props[k];
    });
    return out;
  }

  function applyEdit(wrap, button) {
    var key = wrap.dataset.line;
    var picked = readPopupItems(wrap);

    if (!picked.length) {
      warn((window.variantStrings && window.variantStrings.addToCart_message) || 'Please choose an available option.');
      return Promise.resolve();
    }

    // Same feel as add-to-cart: the popup goes away at once and the cart shows
    // its loading state while Shopify applies the change (two writes when the
    // variant changes, ~1s each on this store), instead of a spinner sitting
    // on the popup button.
    button.classList.add('is-loading');
    button.disabled = true;
    document.body.classList.remove('edit-cart-show');
    setLoading(true);
    window.__civiaCartHold = true; // the background sync waits until both writes are done

    return findLine(key)
      .then(function (found) {
        var cart = found.cart;
        var line = found.line;
        if (!line) {
          // The line is gone (removed in another tab, or already replaced) - nothing to edit.
          var error = new Error('This item is no longer in your cart.');
          error.handled = true;
          throw error;
        }

        var props = lineProperties(line);
        var sameQty = 0;
        var toAdd = [];
        var realTotal = 0;

        picked.forEach(function (p) {
          if (p.id === line.variant_id) {
            sameQty += p.quantity;
            realTotal += line.original_price * p.quantity;
          } else {
            var entry = { id: p.id, quantity: p.quantity };
            if (Object.keys(props).length) entry.properties = props;
            if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
              entry.selling_plan = line.selling_plan_allocation.selling_plan.id;
            }
            toAdd.push(entry);
            realTotal += (p.price !== null ? p.price : line.original_price) * p.quantity;
          }
        });

        var gift = null;
        cart.items.forEach(function (i) {
          if (i.variant_id === GIFT_VARIANT_ID) gift = i;
          else if (i.key !== line.key) realTotal += i.original_line_price;
        });
        var eligible = realTotal >= MIN_CART_TOTAL;

        if (eligible && !gift) toAdd.push({ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } });

        var updates = {};
        if (sameQty !== line.quantity) updates[line.key] = sameQty;
        if (!eligible && gift) updates[gift.key] = 0;
        var needsUpdate = Object.keys(updates).length > 0;

        // Step 1: add the newly chosen variant(s). Only carries the sections when it is the last write.
        var first = toAdd.length
          ? post('cart/add.js', needsUpdate ? { items: toAdd } : withSections({ items: toAdd }))
          : Promise.resolve(null);

        return first.then(function (added) {
          // Step 2: adjust or remove the original line (and the stud if it no longer qualifies), by key.
          if (needsUpdate) return post('cart/update.js', withSections({ updates: updates }));
          return added;
        });
      })
      .then(function (data) {
        if (data && data.items && typeof data.item_count === 'number') window.__civiaCartSnapshot = data;
        if (data && data.sections) renderSections(data.sections, data.items && typeof data.item_count === 'number' ? data : null);
      })
      .catch(function (error) {
        // The original line is untouched on any failure (it is only changed after
        // the add succeeded), so a warning is enough; the cart stays as it was.
        if (error && error.handled) {
          warn(error.message);
        } else {
          console.error('Cart edit failed:', error);
          warn('Something went wrong. Please try again.');
        }
      })
      .then(function () {
        setLoading(false);
        button.classList.remove('is-loading');
        button.disabled = false;
        window.__civiaCartHold = false;
        if (typeof window.civiaCartSync === 'function') window.civiaCartSync();
      });
  }

  // Capture phase, so this runs before the theme's delegated jQuery handler
  // (assets/theme.js, halo.addAllItemCartEdit) and stopping propagation keeps
  // that handler - which depends on the old removeItem callback - from running.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('#add-all-to-cart, [data-update-cart-edit]');
    if (!button) return;
    var popup = button.closest('[data-edit-cart-popup]');
    var wrap = popup && popup.querySelector('[data-template-cart-edit]');
    if (!wrap) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (button.disabled || button.classList.contains('is-loading')) return;
    applyEdit(wrap, button);
  }, true);

  function install() {
    var S = window.Shopify;
    if (!S || typeof S.changeItem !== 'function') return false;
    // Signature kept identical to global.js: (variant_id, quantity, line, callback).
    // The theme's callbacks only re-fetched the markup we already have, so they are
    // not invoked; the three callers in theme.js (remove, quantity, edit popup) are
    // all handled here.
    S.changeItem = function (variantId, quantity, line) { write(line, quantity); };
    S.removeItem = function (variantId, line) { write(line, 0); };
    return true;
  }

  window.civiaCart = { sectionIds: sectionIds, renderSections: renderSections, pageSectionId: pageSectionId, write: write, writeQuantity: writeQuantity };

  if (!install()) {
    document.addEventListener('DOMContentLoaded', install);
    window.addEventListener('load', install);
  }
})();
